import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Play } from 'lucide-react';

const DarkFeatureDetail = () => {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [paused, setPaused] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setLoading(true);
          video.src = '/feature-audio.mp4';
          video.load();
          video.play().catch(() => {});
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  const togglePlayPause = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play();
      setPaused(false);
    } else {
      video.pause();
      setPaused(true);
    }
  };

  const features = Array.isArray(t('featureSections.audioFormat.features', { returnObjects: true }))
    ? (t('featureSections.audioFormat.features', { returnObjects: true }) as string[])
    : [
        'Instant AI transcription with 99% accuracy',
        'Smart summarization and key point extraction',
        'YouTube video summarization capability',
      ];

  return (
    <section className="bg-navy py-24">
      <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-16 items-center">
        <div>
          <h2 className="text-4xl font-bold text-white mb-6">
            {t('home.darkFeature.title', 'Captures notes automatically')}
          </h2>
          <ul className="space-y-4">
            {features.map((feature, index) => (
              <li key={index} className="flex gap-3 text-gray-300">
                <Check className="text-primary w-5 h-5 mt-0.5 flex-shrink-0" />
                <div>
                  <strong className="text-white">{feature}</strong>
                </div>
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-navy-card rounded-2xl p-4">
          <div className="relative cursor-pointer" onClick={togglePlayPause}>
            <video
              ref={videoRef}
              loop
              muted
              playsInline
              className="rounded-xl w-full"
              onCanPlay={() => setLoading(false)}
            />
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-black/40">
                <div className="w-10 h-10 border-4 border-white/30 border-t-white rounded-full animate-spin" />
              </div>
            )}
            {paused && !loading && (
              <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-black/30">
                <div className="bg-white/20 backdrop-blur-sm rounded-full p-4">
                  <Play className="w-8 h-8 text-white fill-white" />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default DarkFeatureDetail;
